import { createRequest } from "../request";
import { config } from "../config/env";
import type {
  ChangePasswordParams,
  UserBasicInfo,
  UserBaseInfoParams,
  UserInfo,
  UserInfoParams,
} from "./types/auth";

const api = createRequest({
  baseURL: config.apiBaseUrl,
});

export interface LoginParams {
  /**
   * ADVISOR_SMART_PORTAL :ADVISOR_SMART_PORTAL
   * BROKERAGE_BUSINESS_MANAGEMENT :BROKERAGE_BUSINESS_MANAGEMENT
   */
  app: string;
  password: string;
  username: string;
  [property: string]: any;
}

export interface LoginResult {
  token: string;
  uid?: number | string;
  userInfo?: {
    id?: string | number;
    username?: string;
    name?: string;
    avatar?: string;
    roles?: string[];
  };
}

/**
 * 用户登录
 * POST /api/user/login
 */
export function login(data: LoginParams): Promise<LoginResult> {
  return api.post<LoginResult>("/user/login", undefined, { params: data });
}

/**
 * 获取用户基本信息（无需登录）
 * GET /api/v1/user/info-base
 */
export function getUserBaseInfo(
  params: UserBaseInfoParams
): Promise<UserBasicInfo> {
  return api.get<UserBasicInfo>("/user/info-base", { params });
}

/**
 * 获取用户公共信息
 * GET /api/v1/user/info
 */
export function getUserInfo(params: UserInfoParams): Promise<UserInfo> {
  return api.get<UserInfo>("/user/info", { params });
}

/**
 * 更改密码
 * POST /api/v1/user/change-password
 */
export function changePassword(data: ChangePasswordParams): Promise<boolean> {
  return api.post<boolean>("/user/change-password", data);
}

/**
 * 用户登出
 * POST /api/user/logout
 */
export function logout(): Promise<void> {
  return api.post<void>("/logout");
}

// ==================== SSO 统一登录相关 ====================

export interface SSOLoginParams {
  /** 用户名 */
  username: string;
  /** 密码 */
  password: string;
  /**
   * 应用标识:
   * ADVISOR_SMART_PORTAL | BROKERAGE_BUSINESS_MANAGEMENT | SMART_H5 | SMART_APP
   */
  app: string;
}

export interface SSOLoginResult {
  /** 授权码，用于换取 token */
  code: string;
}

export interface ExchangeTokenParams {
  /** 授权码 */
  code: string;
  /** 登录方式：password / bibl */
  provider?: string;
  /** 应用标识 */
  app?: string;
}

export interface ExchangeTokenResult {
  /** 用户ID */
  uid: number;
  /** 登录令牌 */
  token: string;
}

/**
 * SSO 登录，获取授权码
 * POST /api/v1/sso/code
 * @param params Query 参数
 */
export function ssoLogin(params: SSOLoginParams): Promise<SSOLoginResult> {
  return api.post<SSOLoginResult>("/sso/code", undefined, { params });
}

/**
 * 用授权码换取 token
 * GET /api/v1/sso/code2token
 * @param params Query 参数
 */
export function exchangeToken(
  params: ExchangeTokenParams
): Promise<ExchangeTokenResult> {
  return api.get<ExchangeTokenResult>("/sso/code2token", {
    params,
    manualErrorHandling: true,
  });
}

export interface SSOProviderInfo {
  /** 登录方式标识：password / bibl */
  provider: string;
  /** 展示名称 */
  name: string;
  /** 是否启用 */
  enabled: boolean;
  /** 第三方跳转地址，password 通常为 null */
  redirectUrl: string | null;
}

export type SSOProviderMap = Record<string, SSOProviderInfo>;

/**
 * 获取当前租户可用的登录方式
 * GET /api/v1/sso/provider
 */
export function getSSOProviders(): Promise<SSOProviderMap> {
  return api.get<SSOProviderMap>("/sso/provider");
}
